/**
 * The ad slot's provider: Google Publisher Tag, the tag Google Ad Manager and
 * AdSense-through-Ad-Manager both serve through.
 *
 * Which ad unit it asks for is `VITE_AD_UNIT`. Without one, a dev server asks
 * Google's public sample network, which serves labelled test creatives on any
 * domain with no account; a production build asks nothing and the slot keeps
 * its placeholder, because the sample network is for testing and not for
 * shipping. A real unit is `/<network code>/<unit>` from an Ad Manager account.
 *
 * The script is loaded on the first showing, not with the page: an ad is the
 * eighth thing a reader does at the earliest, and most never get that far.
 */

/**
 * The sample unit out of Google's own Get Started guide, on their test network
 * 6355419. It serves labelled test creatives to any domain with no account.
 * The guide shows it at 300x250; it fills this slot's 320x50 too, which was
 * checked rather than assumed.
 *
 * https://developers.google.com/publisher-tag/guides/get-started
 */
export const SAMPLE_UNIT = "/6355419/Travel/Europe/France/Paris";

// The canonical host. googletagservices.com and pagead2.googlesyndication.com
// serve the same file, and all three are on the filter lists.
const SCRIPT = "https://securepubads.g.doubleclick.net/tag/js/gpt.js";

/** Long enough for gpt.js on a slow phone; past it the slot shows the banner. */
const LOAD_TIMEOUT_MS = 8000;

/**
 * Long enough for an ad request and its creative; past it the slot shows the
 * banner.
 *
 * This is the case a load timeout does not cover, and the one blockers
 * actually produce. uBlock Origin and its relatives do not block `gpt.js` --
 * they answer the request with a *surrogate*: a stub carrying the whole API,
 * whose `display()` does nothing, precisely so that a page cannot tell. The
 * script loads, `defineSlot` returns something, and `slotRenderEnded` is then
 * never fired by anybody. Without a deadline here the slot waits for an event
 * that is not coming, and sits open and empty forever -- which is exactly what
 * it did.
 *
 * Five seconds against a measured 150-200 ms from the slot opening to the
 * creative, on a desktop with gpt.js already loaded. The margin is for a phone,
 * and the script's own arrival is covered by LOAD_TIMEOUT_MS before this starts.
 */
const RENDER_TIMEOUT_MS = 5000;

/** How the slot ended up. */
export type Fill = "filled" | "empty" | "blocked";

/* The corner of the GPT API this uses, rather than a dependency for its types. */
interface Slot {
  addService(service: PubAds): Slot;
}
interface RenderEnded {
  slot: Slot;
  isEmpty: boolean;
}
interface PubAds {
  addEventListener(event: "slotRenderEnded", listener: (event: RenderEnded) => void): void;
}
interface GoogleTag {
  cmd: Array<() => void> | { push(fn: () => void): void };
  setConfig(config: { safeFrame?: { forceSafeFrame?: boolean } }): void;
  apiReady?: boolean;
  defineSlot(unit: string, size: [number, number], div: string): Slot | null;
  pubads(): PubAds;
  enableServices(): void;
  display(div: string): void;
  destroySlots(slots?: Slot[]): boolean;
}

declare global {
  interface Window {
    googletag?: GoogleTag;
  }
}

/** The unit this build asks for, or null for the placeholder. */
export function configuredUnit(): string | null {
  const unit = import.meta.env["VITE_AD_UNIT"] as string | undefined;
  if (unit) return unit;
  return import.meta.env.DEV ? SAMPLE_UNIT : null;
}

let loading: Promise<GoogleTag | null> | null = null;

/**
 * gpt.js, once. Null when it cannot be had, which is a blocker refusing the
 * request outright -- the loudest of the three ways one shows up here, the
 * others being a surrogate that loads and does nothing (see RENDER_TIMEOUT_MS)
 * and a DNS-level filter, which looks like this one.
 */
function load(): Promise<GoogleTag | null> {
  loading ??= new Promise((resolve) => {
    window.googletag ??= { cmd: [] } as unknown as GoogleTag;
    const script = document.createElement("script");
    script.src = SCRIPT;
    script.async = true;
    // The deadline runs until the queued callback actually runs, not until the
    // script says it loaded. A stub that takes the callback and never calls it
    // would otherwise leave this promise pending for the life of the page, and
    // the slot open and empty behind it.
    const timer = window.setTimeout(() => resolve(null), LOAD_TIMEOUT_MS);
    script.onload = () => {
      const tag = window.googletag!;
      tag.cmd.push(() => {
        window.clearTimeout(timer);
        resolve(tag);
      });
    };
    script.onerror = () => {
      window.clearTimeout(timer);
      resolve(null);
    };
    document.head.appendChild(script);
  });
  return loading;
}

let services = false;
let serial = 0;
let current: Slot | null = null;

/**
 * Ask for one ad into `body`, which is the slot's 320x50 box.
 *
 * A new div and a new slot per showing, destroyed on close, because the slot's
 * content is rewritten every time it opens and GPT holds on to the element it
 * was given.
 */
export async function fillSlot(body: HTMLElement, unit: string): Promise<Fill> {
  const tag = await load();
  if (!tag) return "blocked";
  clearSlot();

  const id = `strip-unit-${++serial}`;
  const div = document.createElement("div");
  div.id = id;
  div.style.cssText = "width:320px;height:50px;";
  body.replaceChildren(div);

  return new Promise((resolve) => {
    // Resolved once, by whichever comes first: the render, or the deadline.
    let done = false;
    const settle = (fill: Fill): void => {
      if (done) return;
      done = true;
      resolve(fill);
    };
    window.setTimeout(() => settle("blocked"), RENDER_TIMEOUT_MS);

    tag.cmd.push(() => {
      const slot = tag.defineSlot(unit, [320, 50], id);
      if (!slot) return settle("empty");
      slot.addService(tag.pubads());
      tag.pubads().addEventListener("slotRenderEnded", (event) => {
        if (event.slot === slot) settle(event.isEmpty ? "empty" : "filled");
      });
      if (!services) {
        // Cross-domain rendering, which Google's CSP guide requires: a
        // same-domain creative inherits this page's Content-Security-Policy,
        // GPT cannot control what is inside a creative, and the ones that use
        // an inline script are then refused. In a SafeFrame the creative has
        // its own origin and its own policy.
        // https://developers.google.com/publisher-tag/guides/content-security-policy
        tag.setConfig({ safeFrame: { forceSafeFrame: true } });
        tag.enableServices();
        services = true;
      }
      current = slot;
      tag.display(id);
    });
  });
}

/** Let go of the showing ad, if there is one. */
export function clearSlot(): void {
  if (current && window.googletag?.destroySlots) window.googletag.destroySlots([current]);
  current = null;
}
